import React, { useEffect, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { useAuth0 } from '@auth0/auth0-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getPlan } from './config/plans';
import { apiUrl } from './hooks/useOnboardingStatus';
import { capture } from './analytics';

function CheckoutForm({ subscription = null, refreshOnboarding }) {
  const stripe = useStripe();
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlan = getPlan(searchParams.get('plan'));
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoStatus, setPromoStatus] = useState('');
  const [changeComplete, setChangeComplete] = useState(false);

  const hasActiveSubscription = ['active', 'trialing'].includes(
    subscription?.status,
  );
  const hasUsedTrial =
    subscription?.trialAvailable === false ||
    (subscription?.trialAvailable !== true &&
      ['granted', 'denied'].includes(subscription?.trialEligibilityStatus));

  useEffect(() => {
    if (!selectedPlan) navigate('/pricing', { replace: true });
  }, [navigate, selectedPlan]);

  if (!isAuthenticated || !user?.email_verified) {
    return (
      <div>
        <h2>Please Verify Your Email</h2>
        <p>
          You need to verify your email address before proceeding. Please check
          your inbox for a verification email.
        </p>
      </div>
    );
  }

  if (!selectedPlan) return null;

  const getAccessToken = () =>
    getAccessTokenSilently({
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      },
    });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!hasActiveSubscription && !stripe) return;

    setProcessing(true);
    setErrorMessage('');

    try {
      const token = await getAccessToken();
      const response = await fetch(apiUrl('/payment/create-checkout-session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan: selectedPlan.id,
          promoCode: hasActiveSubscription ? '' : promoCode.trim(),
        }),
      });
      const result = await response.json().catch(() => ({}));

      if (response.ok && result.action) {
        capture('subscription_plan_changed', {
          plan: selectedPlan.id,
          changed: result.action === 'plan_changed',
        });
        if (refreshOnboarding) await refreshOnboarding();
        setChangeComplete(true);
        return;
      }

      if (response.ok && result.id) {
        const checkoutMode = result.checkoutMode || 'trial';
        capture(
          checkoutMode === 'trial'
            ? 'trial_checkout_started'
            : 'paid_checkout_started',
          { plan: selectedPlan.id },
        );
        localStorage.setItem('last_checkout_session_id', result.id);
        localStorage.setItem('last_checkout_mode', checkoutMode);
        const { error: stripeError } = await stripe.redirectToCheckout({
          sessionId: result.id,
        });
        if (stripeError) setErrorMessage(stripeError.message);
        return;
      }

      if (result.error === 'beta_full') {
        capture('beta_full', { plan: selectedPlan.id });
      }
      const messages = {
        beta_full: 'The 100-seat public beta is full.',
        temporary_email_not_allowed: 'Please use a permanent email address to start a subscription. Contact support@virgil.com.ai if you need help.',
        checkout_rate_limited: 'Too many checkout attempts. Please wait up to an hour before trying again, or contact support@virgil.com.ai for help.',
        checkout_in_progress:
          'Checkout is already starting for this account. Wait a moment and try again.',
        invalid_plan: 'Please choose one of the listed plans.',
        email_not_verified: 'Verify your email before continuing.',
        account_blocked: 'Access to this account is paused. Contact Virgil support for help.',
        payment_review_required: 'Contact Virgil support before trying another checkout so we can review the previous payment.',
          cvc_check_failed: 'Your bank could not verify the card security code. Return to pricing and try checkout with the correct code or another card.',
        subscription_not_active:
          'Your subscription changed while this page was open. Return to pricing and try again.',
        promo_not_supported_for_plan_change:
          'Promotion codes cannot be added while changing an active plan.',
        complimentary_discount_incomplete:
          'Your current complimentary discount is not valid for every part of this plan.',
        rate_limit_hash_salt_not_configured:
          'Checkout is temporarily unavailable because of a server configuration problem. Please try again shortly.',
      };
      setErrorMessage(
        messages[result.error] ||
          'Checkout is unavailable right now. Please try again.',
      );
    } catch (error) {
      setErrorMessage('Checkout is unavailable right now. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleApplyPromo = async (event) => {
    event.preventDefault();
    setPromoStatus('');
    setErrorMessage('');
    if (!promoCode.trim()) {
      setErrorMessage('Enter a promotion code first.');
      return;
    }

    try {
      const token = await getAccessToken();
      const response = await fetch(apiUrl('/payment/validate-promo'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ promoCode: promoCode.trim() }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'invalid_promotion_code');
      setPromoStatus('Promotion code accepted. Stripe will show the discount.');
    } catch (error) {
      setPromoStatus('');
      setErrorMessage(
        error?.message === 'promo_rate_limited'
          ? 'Too many promotion-code attempts were made. Please wait before trying again.'
          : 'That promotion code is not valid.',
      );
    }
  };

  if (changeComplete) {
    return (
      <div className="checkout-container">
        <div className="checkout-form checkout-complete" role="status">
          <h1 className="checkout-title">
            {selectedPlan.name} is now your plan
          </h1>
          <p className="checkout-billing-note">
            The change is active now. Stripe will apply any prorated adjustment
            to your next invoice.
          </p>
          <Link to="/account" className="checkout-submit-button">
            Back to your account
          </Link>
        </div>
      </div>
    );
  }

  const title = hasActiveSubscription
    ? `Change to ${selectedPlan.name}`
    : hasUsedTrial
      ? `Subscribe to ${selectedPlan.name}`
      : 'Start your 14-day trial';
  const billingNote = hasActiveSubscription
    ? 'Your plan changes immediately. Stripe applies any prorated credit or charge to your next invoice.'
    : hasUsedTrial
      ? 'Your one-time trial has already been used. Stripe will start this paid monthly subscription when checkout is complete.'
      : 'Card required. Your trial includes up to $20 of AI usage with no overage. After 14 days, this monthly plan starts automatically unless you cancel.';

  return (
    <div className="checkout-container">
      <div className="checkout-nav">
        <Link to="/pricing" className="back-link">
          <span>←</span> CHANGE PLAN
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="checkout-form">
        <h1 className="checkout-title">{title}</h1>
        <p className="checkout-billing-note">{billingNote}</p>

        <div className="checkout-plan-box selected checkout-plan-summary">
          <span className="checkout-plan-selection" aria-hidden="true">
            Selected plan
          </span>
          <div className="checkout-plan-name">
            {selectedPlan.name.toUpperCase()}
          </div>
          <div className="checkout-plan-price">{selectedPlan.priceLabel}</div>
          <div className="checkout-plan-description">
            {selectedPlan.description}
          </div>
        </div>

        {!hasActiveSubscription && (
          <div className="checkout-promo-container">
            <input
              type="text"
              placeholder="Promo Code"
              value={promoCode}
              onChange={(event) => setPromoCode(event.target.value)}
              className="checkout-promo-input"
            />
            <button
              type="button"
              className="checkout-promo-button"
              onClick={handleApplyPromo}
            >
              APPLY
            </button>
          </div>
        )}
        {promoStatus && (
          <p className="form-success" role="status">{promoStatus}</p>
        )}
        {errorMessage && (
          <div className="checkout-error-message" role="alert">
            {errorMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={processing || (!stripe && !hasActiveSubscription)}
          className="checkout-submit-button"
        >
          {processing
            ? 'Saving your plan…'
            : hasActiveSubscription
              ? `Change to ${selectedPlan.name}`
              : 'Continue to secure checkout'}
        </button>
      </form>
    </div>
  );
}

export default CheckoutForm;
